// ============================================================================
// CHAT API — SSE streaming + React Query hooks for AI conversations
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, API_BASE } from './client'
import { getAccessToken, refreshAccessToken, forceLogout } from '@/contexts/AuthContext'
import { getSentrySessionId } from '@/lib/sentry'
import { RENDER_STATUSES, type RenderPayload, type RenderStatus } from '@/types/chatRenders'

// ============================================================================
// TYPES
// ============================================================================

export interface ConversationSubjectProfile {
  profile_id: number
  user_id: number | null
  name: string
}

export interface Conversation {
  id: number
  title: string | null
  last_message: string
  updated_at: string
  message_count: number
  total_tokens: number
  is_archived: boolean
  subject_profile?: ConversationSubjectProfile
}

export interface ChatMessageToolCallData {
  tool: string
  args: Record<string, unknown>
  tool_call_id?: string
}

export interface ChatMessageToolResultData {
  tool: string
  result: unknown
  tool_call_id?: string
}

// ----------------------------------------------------------------------------
// FILE ATTACHMENTS
// ----------------------------------------------------------------------------

/** A file attached to a chat turn. `data_base64` may be a raw base64 string
 *  or a `data:<mime>;base64,...` URI — the backend strips the header. */
export interface ChatAttachment {
  filename: string
  media_type: string
  data_base64: string
}

/** Accepted attachment media types — kept in sync with the backend
 *  (`ai_agent/attachments.py`). */
export const ACCEPTED_ATTACHMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/csv',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ant.fit',
] as const

/** `accept` attribute for the file picker. */
export const ATTACHMENT_ACCEPT = '.pdf,image/png,image/jpeg,image/webp,.csv,.txt,.xlsx,.docx,.fit,.zwo'

/** Per-type max sizes (bytes), mirroring the backend limits. */
export const ATTACHMENT_MAX_PDF_BYTES = 25 * 1024 * 1024
export const ATTACHMENT_MAX_IMAGE_BYTES = 10 * 1024 * 1024
export const ATTACHMENT_MAX_TEXT_BYTES = 5 * 1024 * 1024
export const MAX_ATTACHMENTS = 4

export interface ChatMessageData {
  role: 'user' | 'assistant'
  content: string
  created_at: string
  message_type: 'text' | 'tool_call' | 'tool_result' | 'tool_calls' | 'tool_results'
  model_used?: string
  token_count?: number
  tool_name?: string
  tool_args?: Record<string, unknown>
  tool_result?: unknown
  tool_call_id?: string
  tool_calls?: ChatMessageToolCallData[]
  tool_results?: ChatMessageToolResultData[]
}

export interface SSETokenEvent {
  content: string
}

export interface SSEToolCallEvent {
  tool: string
  args: Record<string, unknown>
  replace_content?: string
  injected?: boolean
}

export interface SSEToolResultEvent {
  tool: string
  result: unknown
}

/** First frame on the wire, before any LLM work. Carries the ids a client needs
 *  to correlate the turn. `complete` carries conversation_id too, but a turn
 *  that errors, is superseded or is stopped never reaches `complete` — a client
 *  that only learned the id there could not name the thread it just lost. */
export interface SSEStartEvent {
  conversation_id: number
  stream_id: string
}

export interface SSECompleteEvent {
  conversation_id: number
  message: string | null
  /** Optional suggested follow-up prompts for the turn. */
  followups?: string[]
}

export interface SSEErrorEvent {
  error: string
  code: string
  partial?: string
}

export type SSEEvent =
  | { type: 'start'; data: SSEStartEvent }
  | { type: 'token'; data: SSETokenEvent }
  | { type: 'tool_call'; data: SSEToolCallEvent }
  | { type: 'tool_result'; data: SSEToolResultEvent }
  | { type: 'complete'; data: SSECompleteEvent }
  | { type: 'error'; data: SSEErrorEvent }

// ----------------------------------------------------------------------------
// RENDER PAYLOAD EXTRACTION
// Tool result dicts may carry an optional `render` payload (result.render)
// describing a rich chat card. These helpers are pure so both the live SSE
// path and hydration from persisted tool_results share one parser.
// ----------------------------------------------------------------------------

/** Tolerate results that arrive as JSON strings (some tools stringify). */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value) as unknown
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null
    } catch {
      return null
    }
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return null
}

/**
 * Extract and normalize a RenderPayload from a tool result dict.
 * Returns null when the result carries no (or a malformed) render — callers
 * then fall back to the plain ToolCallCard pill.
 */
export function extractRenderPayload(result: unknown): RenderPayload | null {
  const record = asRecord(result)
  const render = record ? asRecord(record.render) : null
  if (!render || typeof render.type !== 'string' || !render.type) return null

  const status: RenderStatus = (RENDER_STATUSES as readonly string[]).includes(
    render.status as string
  )
    ? (render.status as RenderStatus)
    : 'updated'

  const entityRec = asRecord(render.entity) ?? {}
  const rawId = entityRec.id
  const entity = {
    kind: typeof entityRec.kind === 'string' ? entityRec.kind : '',
    id: typeof rawId === 'number' || typeof rawId === 'string' ? rawId : null,
    title: typeof entityRec.title === 'string' ? entityRec.title : '',
    date: typeof entityRec.date === 'string' ? entityRec.date : null,
  }

  return {
    type: render.type,
    status,
    entity,
    spec: asRecord(render.spec) ?? {},
  }
}

/**
 * Best-effort extraction of widget code from a tool result dict, so a `chart`
 * render can mount the sandboxed widget inline without a refetch. Looks at
 * result.code / result.widget.code / result.widget_code.
 */
export function extractWidgetCode(result: unknown): string | null {
  const record = asRecord(result)
  if (!record) return null
  if (typeof record.code === 'string' && record.code.trim()) return record.code
  if (typeof record.widget_code === 'string' && record.widget_code.trim()) return record.widget_code
  const widget = asRecord(record.widget)
  if (widget && typeof widget.code === 'string' && widget.code.trim()) return widget.code
  return null
}

// ============================================================================
// SSE STREAMING
// ============================================================================

/**
 * Stream chat response from the interactive agent via SSE.
 * Uses native fetch (not EventSource) since we need POST + JWT headers.
 */
export async function streamChat(
  message: string,
  conversationId: number | null,
  targetProfileId: number | null | undefined,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal,
  attachments?: ChatAttachment[],
  viewId?: number | null,
  /** Base64 data-URI voice clip. The backend transcribes it and folds the
   *  transcript into `message` (may be empty for a voice-only turn). */
  audio?: string,
  /** Scope the turn to one training plan, so the program and schedule tools
   *  target it. A plan the caller does not own is REJECTED by the stream, not
   *  silently ignored, so never retry the same turn without it. */
  planId?: number | null,
  /** The team the coach is looking at. The backend resolves the squad from it
   *  (a coach with several teams otherwise gets no squad at all), so Flow
   *  inherits the team instead of guessing. */
  groupUuid?: string | null
): Promise<void> {
  const token = getAccessToken()

  // The stream registers server-side under this session id (SentryTracing
  // middleware parses the baggage header), so a stop button can later cancel
  // the in-flight turn via the same session key.
  const sessionId = getSentrySessionId()

  const response = await fetch(`${API_BASE}/api/v1/ai/interactive/stream/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(sessionId && sessionId !== 'server' && sessionId !== 'unavailable'
        ? { baggage: `sentry-session_id=${sessionId}` }
        : {}),
    },
    body: JSON.stringify({
      message,
      conversation_id: conversationId,
      ...(targetProfileId ? { target_profile_id: targetProfileId } : {}),
      ...(attachments && attachments.length ? { attachments } : {}),
      ...(audio ? { audio } : {}),
      // When the chat is launched from a dashboard, the backend injects the
      // current view as context so the views/widgets tools target it.
      ...(viewId ? { view_id: viewId } : {}),
      ...(planId ? { plan_id: planId } : {}),
      ...(groupUuid ? { group_uuid: groupUuid } : {}),
    }),
    signal,
  })

  if (!response.ok) {
    if (response.status === 401) {
      try {
        await refreshAccessToken()
        return streamChat(message, conversationId, targetProfileId, onEvent, signal, attachments, viewId, audio, planId, groupUuid)
      } catch {
        forceLogout('chat_stream_refresh_failed')
        return
      }
    }
    onEvent({
      type: 'error',
      data: { error: `HTTP ${response.status}: ${response.statusText}`, code: 'HTTP_ERROR' },
    })
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    onEvent({ type: 'error', data: { error: 'No response body', code: 'NO_BODY' } })
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // Parse SSE: split on double newlines
      const parts = buffer.split('\n\n')
      // Keep the last incomplete chunk in the buffer
      buffer = parts.pop() ?? ''

      for (const part of parts) {
        const parsed = parseSSEBlock(part)
        if (parsed) {
          onEvent(parsed)
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      const parsed = parseSSEBlock(buffer)
      if (parsed) {
        onEvent(parsed)
      }
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Parse a single SSE block (event: type\ndata: json).
 */
function parseSSEBlock(block: string): SSEEvent | null {
  let eventType = ''
  let dataStr = ''

  for (const line of block.split('\n')) {
    if (line.startsWith('event: ')) {
      eventType = line.slice(7).trim()
    } else if (line.startsWith('data: ')) {
      dataStr = line.slice(6)
    } else if (line.startsWith(':')) {
      // SSE comment (flush hint), ignore
      continue
    }
  }

  if (!eventType || !dataStr) return null

  try {
    const data = JSON.parse(dataStr) as Record<string, unknown>
    return { type: eventType, data } as unknown as SSEEvent
  } catch {
    return null
  }
}

// ============================================================================
// REACT QUERY HOOKS
// ============================================================================

const CONVERSATIONS_KEY = ['conversations']

export function useConversations(targetProfileId?: number | null) {
  return useQuery({
    queryKey: [...CONVERSATIONS_KEY, targetProfileId ?? 'self'],
    queryFn: async () => {
      const { data } = await apiClient.get<Conversation[]>('/ai/conversations/', {
        params: targetProfileId ? { target_profile_id: targetProfileId } : undefined,
      })
      return data
    },
    retry: false,
  })
}

export function useConversationMessages(conversationId: number | null, targetProfileId?: number | null) {
  return useQuery({
    queryKey: ['conversations', targetProfileId ?? 'self', conversationId, 'messages'],
    queryFn: async () => {
      const { data } = await apiClient.get<ChatMessageData[]>(
        `/ai/conversations/${conversationId}/messages/`,
        { params: targetProfileId ? { target_profile_id: targetProfileId } : undefined },
      )
      return data
    },
    enabled: !!conversationId,
  })
}

export function useDeleteConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (conversationId: number) => {
      await apiClient.delete(`/ai/conversations/${conversationId}/`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY })
    },
  })
}

/**
 * Invalidate conversation queries (call after sending a message).
 */
export function useInvalidateConversations(targetProfileId?: number | null) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: [...CONVERSATIONS_KEY, targetProfileId ?? 'self'], exact: true })
  }
}
